import { Course } from '@/types/courseTypes';

export const jestAdvancedCourse: Course = {
  id: 'jest-advanced-testing',
  title: 'Advanced Testing with Jest',
  description: 'Master advanced Jest testing patterns, mocking strategies, and test architecture.',
  level: 'Advanced',
  totalDuration: '6 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['JavaScript testing basics', 'Jest fundamentals', 'Node.js knowledge'],
  learningOutcomes: [
    'Implement complex mocking strategies',
    'Design maintainable test architectures',
    'Master async testing patterns',
    'Optimize test performance and coverage'
  ],
  modules: [
    {
      id: 'jest-advanced-mocking',
      title: 'Advanced Mocking Strategies',
      description: 'Complex mocking patterns and dependency management',
      content: `## Advanced Mocking Strategies in Jest

Effective testing requires isolating units of code from their dependencies. Jest provides powerful mocking capabilities that allow you to create controlled testing environments and verify complex interactions between components.

### Understanding Jest Mocking Fundamentals

**Mock Functions**: Jest mock functions (jest.fn()) capture calls, return values, and provide complete control over function behavior. They serve as spies, stubs, and mocks in your testing arsenal.

**Module Mocking**: Jest can mock entire modules, allowing you to replace external dependencies with controlled implementations during testing.

**Automatic Mocking**: Jest can automatically mock modules in node_modules, providing sensible defaults while allowing customization where needed.

### Module Mocking Strategies

**Manual Mocks**: Create custom implementations for external dependencies by placing mock files in __mocks__ directories adjacent to the modules being mocked.

**Inline Mocks**: Use jest.mock() to replace modules inline within test files, providing test-specific implementations.

**Partial Mocking**: Mock only specific functions within a module while preserving the original implementation of others using jest.requireActual().

**Dynamic Mocking**: Change mock implementations during test execution to test different scenarios and edge cases.

### Advanced Spy Techniques

**Method Spying**: Use jest.spyOn() to monitor and control methods on existing objects without completely replacing them.

**Implementation Spying**: Track how functions are called while optionally providing custom implementations.

**Call Analysis**: Examine call counts, arguments, return values, and execution contexts to verify component interactions.

**Restored Spies**: Automatically restore original implementations after tests to prevent test pollution.

### Best Practices for Advanced Mocking

**Minimal Mocking**: Mock only what's necessary for the test, avoiding over-mocking that can make tests brittle.

**Realistic Mocks**: Ensure mock behavior closely resembles real implementations to maintain test validity.

**Mock Documentation**: Document mock behavior and expectations to make tests more maintainable.

**Mock Lifecycle Management**: Properly manage mock creation, configuration, and cleanup to prevent memory leaks and test interference.`,
      order: 1,
      estimatedTime: '1.5 hours',
      duration: '1.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Implement complex module mocks',
        'Use spy functions effectively',
        'Handle external dependencies'
      ]
    },
    {
      id: 'jest-async-testing',
      title: 'Async Testing Patterns',
      description: 'Testing asynchronous code and async patterns',
      content: `## Async Testing Patterns in Jest

Testing asynchronous code requires understanding promises, async/await patterns, and handling timing-dependent operations. Jest provides comprehensive tools for testing async operations reliably and efficiently.

### Testing Promises and Async/Await

**Promise-based Testing**: Jest provides multiple ways to test promises, including returning promises from test functions, using resolves/rejects matchers, and async/await syntax.

**Async/Await Pattern**: The most readable approach for testing async operations, allowing you to write synchronous-looking code that properly handles asynchronous operations.

**Error Handling**: Test both successful promise resolution and rejection scenarios to ensure robust error handling in your applications.

### Advanced Async Testing Techniques

**Promise Chains**: Test complex promise chains and ensure proper error propagation through the chain.

**Concurrent Operations**: Test multiple async operations running concurrently using Promise.all(), Promise.race(), and similar patterns.

**Async Iterators**: Test async generators and async iteration patterns common in modern JavaScript applications.

**Custom Promise Utilities**: Test custom promise-based utilities like retry logic, timeout handling, and promise pooling.

### Timer and Delay Testing

**Fake Timers**: Use Jest's fake timer functionality to test time-dependent code without actually waiting for real time to pass.

**Timer Mocking**: Mock setTimeout, setInterval, and other timer functions to test scheduling and timing logic.

**Date Mocking**: Mock Date objects to test time-sensitive functionality with predictable, controllable timestamps.

**Timeout Testing**: Test timeout scenarios and ensure proper cleanup of async operations that may never resolve.

### Testing Async Utilities and Libraries

**HTTP Client Testing**: Test API calls and HTTP client libraries using mocked responses and error scenarios.

**Database Operations**: Test async database operations using mocked database clients and transaction handling.

**File System Operations**: Test async file operations with proper error handling and resource cleanup.

**Event-Driven Async Code**: Test EventEmitter patterns and other event-driven async architectures.

### Best Practices for Async Testing

**Deterministic Testing**: Ensure async tests are deterministic and don't rely on external timing or network conditions.

**Proper Cleanup**: Always clean up async operations, timers, and resources to prevent test pollution and memory leaks.

**Error Boundary Testing**: Test async error scenarios and ensure proper error propagation and handling.

**Performance Considerations**: Be mindful of test execution time when testing async operations and use appropriate timeouts.`,
      order: 2,
      estimatedTime: '1.5 hours',
      duration: '1.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Test async/await patterns',
        'Handle promise chains and errors',
        'Mock timers and intervals'
      ],
      codeExamples: [
        {
          title: "Testing Async/Await Functions",
          code: `// Testing async functions with proper error handling
describe('Async User Service', () => {
  test('should fetch user data successfully', async () => {
    const mockUser = { id: 1, name: 'John Doe' };
    const fetchUser = jest.fn().mockResolvedValue(mockUser);
    
    const result = await fetchUser(1);
    
    expect(result).toEqual(mockUser);
    expect(fetchUser).toHaveBeenCalledWith(1);
  });

  test('should handle fetch errors gracefully', async () => {
    const fetchUser = jest.fn().mockRejectedValue(new Error('Network error'));
    
    await expect(fetchUser(1)).rejects.toThrow('Network error');
  });
});

// Testing promise chains
test('should process data through promise chain', async () => {
  const processData = (data) => 
    Promise.resolve(data)
      .then(d => d.toUpperCase())
      .then(d => d.split(''))
      .then(d => d.reverse().join(''));

  const result = await processData('hello');
  expect(result).toBe('OLLEH');
});`,
          explanation: "Demonstrates testing async functions with proper error handling and promise chains",
          language: "javascript"
        },
        {
          title: "Mocking Timers for Async Operations",
          code: `// Testing time-dependent async operations
describe('Timer-based Operations', () => {
  beforeEach(() => {
    jest.useFakeTimers();
  });

  afterEach(() => {
    jest.useRealTimers();
  });

  test('should execute delayed operation', async () => {
    const callback = jest.fn();
    const delayedExecution = (fn, delay) => {
      setTimeout(fn, delay);
    };

    delayedExecution(callback, 1000);
    
    // Fast-forward time
    jest.advanceTimersByTime(1000);
    
    expect(callback).toHaveBeenCalled();
  });

  test('should handle async timeout operations', async () => {
    const timeoutPromise = new Promise((resolve) => {
      setTimeout(() => resolve('completed'), 500);
    });

    const promise = timeoutPromise;
    jest.advanceTimersByTime(500);
    
    const result = await promise;
    expect(result).toBe('completed');
  });
});`,
          explanation: "Shows how to test timer-dependent async operations using Jest's fake timers",
          language: "javascript"
        }
      ],
      practice: {
        task: "Create a comprehensive test suite for an async data processing service that handles API calls, retries, and timeout scenarios.",
        starter: `// Async Data Service to test
class DataService {
  async fetchAndProcess(url, retries = 3) {
    // Implementation here
  }
  
  async batchProcess(urls, timeout = 5000) {
    // Implementation here
  }
}

// Write comprehensive tests here
describe('DataService', () => {
  // Your tests here
});`,
        solution: `import { DataService } from './DataService';

describe('DataService', () => {
  let dataService;
  let mockFetch;

  beforeEach(() => {
    dataService = new DataService();
    mockFetch = jest.fn();
    global.fetch = mockFetch;
    jest.useFakeTimers();
  });

  afterEach(() => {
    jest.useRealTimers();
    jest.clearAllMocks();
  });

  describe('fetchAndProcess', () => {
    test('should fetch and process data successfully', async () => {
      const mockData = { data: 'test' };
      mockFetch.mockResolvedValue({
        ok: true,
        json: jest.fn().mockResolvedValue(mockData)
      });

      const result = await dataService.fetchAndProcess('http://test.com');

      expect(result).toEqual(mockData);
      expect(mockFetch).toHaveBeenCalledWith('http://test.com');
    });

    test('should retry on failure', async () => {
      mockFetch
        .mockRejectedValueOnce(new Error('Network error'))
        .mockRejectedValueOnce(new Error('Network error'))
        .mockResolvedValue({
          ok: true,
          json: jest.fn().mockResolvedValue({ data: 'success' })
        });

      const result = await dataService.fetchAndProcess('http://test.com', 3);

      expect(mockFetch).toHaveBeenCalledTimes(3);
      expect(result).toEqual({ data: 'success' });
    });

    test('should fail after max retries', async () => {
      mockFetch.mockRejectedValue(new Error('Persistent error'));

      await expect(
        dataService.fetchAndProcess('http://test.com', 2)
      ).rejects.toThrow('Persistent error');

      expect(mockFetch).toHaveBeenCalledTimes(2);
    });
  });

  describe('batchProcess', () => {
    test('should process multiple URLs concurrently', async () => {
      const urls = ['http://test1.com', 'http://test2.com'];
      mockFetch.mockResolvedValue({
        ok: true,
        json: jest.fn().mockResolvedValue({ data: 'test' })
      });

      const promise = dataService.batchProcess(urls);
      jest.advanceTimersByTime(1000);
      
      const results = await promise;

      expect(results).toHaveLength(2);
      expect(mockFetch).toHaveBeenCalledTimes(2);
    });

    test('should handle timeout scenarios', async () => {
      mockFetch.mockImplementation(() => 
        new Promise(resolve => setTimeout(resolve, 10000))
      );

      const promise = dataService.batchProcess(['http://slow.com'], 5000);
      jest.advanceTimersByTime(5000);

      await expect(promise).rejects.toThrow('Timeout');
    });
  });
});`,
        hints: [
          "Use jest.fn() to create mock functions for testing",
          "Remember to use jest.useFakeTimers() for time-dependent tests",
          "Test both success and failure scenarios for async operations",
          "Use mockResolvedValue and mockRejectedValue for promise testing",
          "Don't forget to clean up mocks and timers in afterEach"
        ]
      }
    },
    {
      id: 'jest-test-architecture',
      title: 'Test Architecture Design',
      description: 'Designing maintainable and scalable test suites',
      content: `## Test Architecture Design

Building maintainable and scalable test suites requires thoughtful architecture design. Well-structured tests improve development velocity, reduce maintenance overhead, and provide reliable feedback about code quality.

### Test Organization Principles

**Single Responsibility**: Each test should verify one specific behavior or functionality, making failures easy to diagnose and understand.

**Test Independence**: Tests should run independently without relying on the execution order or state from other tests.

**Descriptive Naming**: Use clear, descriptive names that explain what behavior is being tested and under what conditions.

**Logical Grouping**: Organize tests into logical groups using describe blocks that reflect the structure of your application.

### Test Utility Libraries

**Custom Matchers**: Create domain-specific Jest matchers that make assertions more readable and provide better error messages.

**Helper Functions**: Build reusable test helpers for common operations like data generation, component setup, and assertion patterns.

**Test Builders**: Implement the builder pattern for creating test data and mock objects with sensible defaults and easy customization.

**Mock Factories**: Create factory functions for generating consistent mock objects and test doubles across your test suite.

### Setup and Teardown Patterns

**Global Setup**: Configure test environment settings, database connections, and external service mocks before any tests run.

**Per-Test Setup**: Use beforeEach hooks to establish consistent starting conditions for each test case.

**Cleanup Management**: Implement proper teardown to prevent test pollution and resource leaks between test runs.

**Scoped Setup**: Use describe-level setup for tests that share common configuration while maintaining isolation.

### Test Fixtures and Data Management

**Fixture Libraries**: Create comprehensive fixture libraries with realistic test data that represents various application states.

**Data Factories**: Implement factory functions that generate test data with random variations while maintaining required relationships.

**Snapshot Management**: Use Jest snapshots judiciously for complex object comparisons while avoiding brittle snapshot tests.

**Database Fixtures**: Design database fixture strategies that support parallel test execution and fast test resets.

### Advanced Test Organization

**Test Categories**: Organize tests by type (unit, integration, e2e) and purpose (smoke, regression, performance).

**Parallel Execution**: Structure tests to support parallel execution for faster feedback cycles.

**Test Tagging**: Implement test tagging systems for selective test execution based on features, priorities, or execution time.

**Configuration Management**: Create flexible test configuration systems that support different environments and execution contexts.

### Quality Metrics and Maintenance

**Coverage Analysis**: Use coverage metrics strategically to identify untested code paths while avoiding metric-driven development.

**Test Health Monitoring**: Track test reliability, execution time, and failure patterns to maintain test suite health.

**Refactoring Strategies**: Develop strategies for safely refactoring tests alongside production code.

**Documentation Standards**: Establish documentation standards for complex test scenarios and testing patterns.`,
      order: 3,
      estimatedTime: '1.5 hours',
      duration: '1.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Design test utility libraries',
        'Implement setup/teardown patterns',
        'Create reusable test fixtures'
      ],
      codeExamples: [
        {
          title: "Test Utility Library Design",
          code: `// testUtils.js - Centralized test utilities
export const testUtils = {
  // Custom Jest matchers
  toBeValidEmail: (received) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const pass = emailRegex.test(received);
    return {
      message: () => 
        \`expected \${received} \${pass ? 'not ' : ''}to be a valid email\`,
      pass
    };
  },

  // Test data builders
  userBuilder: (overrides = {}) => ({
    id: Math.random().toString(36),
    name: 'Test User',
    email: 'test@example.com',
    role: 'user',
    createdAt: new Date().toISOString(),
    ...overrides
  }),

  // Mock factories
  createMockApiClient: () => ({
    get: jest.fn(),
    post: jest.fn(),
    put: jest.fn(),
    delete: jest.fn(),
    interceptors: {
      request: { use: jest.fn() },
      response: { use: jest.fn() }
    }
  }),

  // Database helpers
  createTestDb: async () => {
    const db = await setupTestDatabase();
    await db.migrate.latest();
    await db.seed.run();
    return db;
  },

  cleanupTestDb: async (db) => {
    await db.migrate.rollback();
    await db.destroy();
  }
};

// Extend Jest matchers
expect.extend({
  toBeValidEmail: testUtils.toBeValidEmail
});`,
          explanation: "Demonstrates creating a comprehensive test utility library with custom matchers, builders, and helpers",
          language: "javascript"
        },
        {
          title: "Setup and Teardown Patterns",
          code: `// setupTests.js - Global test configuration
import { testUtils } from './testUtils';

// Global setup
beforeAll(async () => {
  // Set up test environment
  process.env.NODE_ENV = 'test';
  
  // Configure global mocks
  global.fetch = jest.fn();
  global.localStorage = {
    getItem: jest.fn(),
    setItem: jest.fn(),
    removeItem: jest.fn(),
    clear: jest.fn()
  };
});

// Global cleanup
afterAll(async () => {
  // Clean up global resources
  jest.clearAllMocks();
  jest.restoreAllMocks();
});

// Example test file with proper setup/teardown
describe('UserService', () => {
  let userService;
  let mockDb;
  let mockLogger;

  beforeAll(() => {
    // Suite-level setup
    mockLogger = {
      info: jest.fn(),
      error: jest.fn(),
      warn: jest.fn()
    };
  });

  beforeEach(async () => {
    // Test-level setup
    mockDb = await testUtils.createTestDb();
    userService = new UserService(mockDb, mockLogger);
    
    // Seed test data
    await mockDb('users').insert([
      testUtils.userBuilder({ id: '1', name: 'John' }),
      testUtils.userBuilder({ id: '2', name: 'Jane' })
    ]);
  });

  afterEach(async () => {
    // Test-level cleanup
    await testUtils.cleanupTestDb(mockDb);
    jest.clearAllMocks();
  });

  describe('findUser', () => {
    test('should find user by id', async () => {
      const user = await userService.findUser('1');
      
      expect(user).toBeDefined();
      expect(user.name).toBe('John');
    });

    test('should handle user not found', async () => {
      const user = await userService.findUser('999');
      
      expect(user).toBeNull();
      expect(mockLogger.warn).toHaveBeenCalledWith(
        'User not found: 999'
      );
    });
  });
});`,
          explanation: "Shows comprehensive setup and teardown patterns for different test scopes",
          language: "javascript"
        }
      ],
      practice: {
        task: "Design a test architecture for a complex e-commerce application with user management, order processing, and payment integration.",
        starter: `// E-commerce system components to test
class UserService {
  constructor(db, emailService) {}
  async createUser(userData) {}
  async findUser(id) {}
  async updateUser(id, updates) {}
}

class OrderService {
  constructor(db, userService, paymentService) {}
  async createOrder(userId, items) {}
  async processOrder(orderId) {}
  async cancelOrder(orderId) {}
}

class PaymentService {
  constructor(paymentGateway) {}
  async processPayment(amount, paymentMethod) {}
  async refund(transactionId, amount) {}
}

// Design comprehensive test architecture here`,
        solution: `// testUtils/index.js
export const testUtils = {
  // User fixtures
  userFixtures: {
    validUser: (overrides = {}) => ({
      id: generateId(),
      email: 'user@example.com',
      name: 'Test User',
      address: {
        street: '123 Test St',
        city: 'Test City',
        zipCode: '12345'
      },
      ...overrides
    }),
    
    adminUser: (overrides = {}) => ({
      ...testUtils.userFixtures.validUser(),
      role: 'admin',
      permissions: ['read', 'write', 'delete'],
      ...overrides
    })
  },

  // Order fixtures
  orderFixtures: {
    validOrder: (overrides = {}) => ({
      id: generateId(),
      userId: generateId(),
      items: [
        { productId: 'p1', quantity: 2, price: 29.99 },
        { productId: 'p2', quantity: 1, price: 49.99 }
      ],
      status: 'pending',
      total: 109.97,
      createdAt: new Date().toISOString(),
      ...overrides
    })
  },

  // Mock factories
  mocks: {
    database: () => ({
      users: {
        insert: jest.fn(),
        findById: jest.fn(),
        update: jest.fn(),
        delete: jest.fn()
      },
      orders: {
        insert: jest.fn(),
        findById: jest.fn(),
        update: jest.fn(),
        findByUserId: jest.fn()
      },
      transaction: jest.fn((callback) => callback({
        commit: jest.fn(),
        rollback: jest.fn()
      }))
    }),

    emailService: () => ({
      sendWelcomeEmail: jest.fn(),
      sendOrderConfirmation: jest.fn(),
      sendPasswordReset: jest.fn()
    }),

    paymentGateway: () => ({
      charge: jest.fn(),
      refund: jest.fn(),
      verifyCard: jest.fn()
    })
  },

  // Test helpers
  helpers: {
    expectValidUser: (user) => {
      expect(user).toHaveProperty('id');
      expect(user).toHaveProperty('email');
      expect(user.email).toBeValidEmail();
      expect(user).toHaveProperty('createdAt');
    },

    expectValidOrder: (order) => {
      expect(order).toHaveProperty('id');
      expect(order).toHaveProperty('userId');
      expect(order).toHaveProperty('items');
      expect(Array.isArray(order.items)).toBe(true);
      expect(order).toHaveProperty('total');
      expect(typeof order.total).toBe('number');
    }
  }
};

// testSetup.js - Global test configuration
import { testUtils } from './testUtils';

let testDb;

global.beforeAll(async () => {
  testDb = await setupTestDatabase();
  await testDb.migrate.latest();
});

global.afterAll(async () => {
  await testDb.destroy();
});

global.beforeEach(async () => {
  await testDb.raw('TRUNCATE TABLE users, orders, payments RESTART IDENTITY CASCADE');
});

// Example service test with architecture
describe('UserService Integration Tests', () => {
  let userService;
  let mockEmailService;
  let mockDb;

  beforeEach(() => {
    mockDb = testUtils.mocks.database();
    mockEmailService = testUtils.mocks.emailService();
    userService = new UserService(mockDb, mockEmailService);
  });

  describe('createUser', () => {
    test('should create user with valid data', async () => {
      const userData = testUtils.userFixtures.validUser();
      mockDb.users.insert.mockResolvedValue(userData);
      
      const result = await userService.createUser(userData);
      
      testUtils.helpers.expectValidUser(result);
      expect(mockEmailService.sendWelcomeEmail).toHaveBeenCalledWith(userData.email);
    });

    test('should handle duplicate email error', async () => {
      const userData = testUtils.userFixtures.validUser();
      mockDb.users.insert.mockRejectedValue(new Error('Email already exists'));
      
      await expect(userService.createUser(userData)).rejects.toThrow('Email already exists');
    });
  });
});

// Integration test example
describe('Order Processing Integration', () => {
  let orderService;
  let userService;
  let paymentService;
  
  beforeEach(async () => {
    const mockDb = testUtils.mocks.database();
    const mockEmailService = testUtils.mocks.emailService();
    const mockPaymentGateway = testUtils.mocks.paymentGateway();
    
    userService = new UserService(mockDb, mockEmailService);
    paymentService = new PaymentService(mockPaymentGateway);
    orderService = new OrderService(mockDb, userService, paymentService);
  });

  test('should process complete order workflow', async () => {
    // Create user
    const user = await userService.createUser(testUtils.userFixtures.validUser());
    
    // Create order
    const orderData = testUtils.orderFixtures.validOrder({ userId: user.id });
    const order = await orderService.createOrder(user.id, orderData.items);
    
    // Process payment and order
    const result = await orderService.processOrder(order.id);
    
    testUtils.helpers.expectValidOrder(result);
    expect(result.status).toBe('completed');
  });
});`,
        hints: [
          "Create separate fixture files for different domain objects",
          "Use factory functions for generating test data with sensible defaults",
          "Implement helper functions for common assertions",
          "Design mock factories that are reusable across test files",
          "Structure tests to mirror your application architecture",
          "Use describe blocks to group related functionality",
          "Implement proper cleanup to prevent test pollution"
        ]
      }
    },
    {
      id: 'jest-performance-optimization',
      title: 'Test Performance & Coverage',
      description: 'Optimizing test performance and achieving meaningful coverage',
      content: `## Test Performance & Coverage Optimization

Efficient test suites provide fast feedback while maintaining comprehensive coverage. Understanding performance bottlenecks and coverage analysis helps teams maintain high-quality, maintainable test suites.

### Test Performance Optimization

**Execution Speed Analysis**: Profile test execution to identify slow tests and performance bottlenecks that impact development workflow.

**Parallel Execution**: Configure Jest to run tests in parallel across multiple workers, maximizing CPU utilization and reducing total execution time.

**Test Isolation**: Ensure tests can run independently to support parallel execution without race conditions or shared state issues.

**Resource Management**: Optimize memory usage and prevent memory leaks that can slow down test execution over time.

### Module Loading Optimization

**Module Mocking Strategy**: Use strategic module mocking to avoid loading unnecessary dependencies and reduce test setup overhead.

**Transform Configuration**: Optimize Jest transform configuration to minimize file processing time for TypeScript, JSX, and other transpiled code.

**Cache Management**: Leverage Jest's cache system effectively while knowing when to clear cache for consistent results.

**Bundle Optimization**: Minimize the amount of code loaded during test execution by mocking large dependencies and external libraries.

### Coverage Analysis and Strategy

**Meaningful Metrics**: Focus on coverage metrics that provide value rather than chasing 100% coverage without considering code quality.

**Branch Coverage**: Prioritize branch coverage over line coverage to ensure all code paths and decision points are tested.

**Coverage Exclusions**: Configure appropriate coverage exclusions for generated code, type definitions, and infrastructure code.

**Differential Coverage**: Implement differential coverage analysis to focus on changes rather than absolute coverage numbers.

### Quality Gates and CI Integration

**Performance Thresholds**: Establish test execution time thresholds and fail builds when tests become too slow.

**Coverage Requirements**: Set minimum coverage thresholds that balance quality assurance with development velocity.

**Flaky Test Detection**: Implement monitoring to detect and address flaky tests that undermine confidence in the test suite.

**Test Reporting**: Generate comprehensive test reports that provide actionable insights for maintaining test quality.

### Advanced Performance Techniques

**Test Sharding**: Implement test sharding strategies for very large test suites that need to run across multiple machines.

**Smart Test Selection**: Use test impact analysis to run only tests affected by code changes during development.

**Snapshot Optimization**: Optimize snapshot tests to minimize serialization overhead and storage requirements.

**Database Test Optimization**: Implement efficient database testing strategies with proper transaction management and data seeding.

### Monitoring and Maintenance

**Test Health Metrics**: Track test reliability, execution time trends, and failure patterns over time.

**Performance Regression Detection**: Implement monitoring to detect when test performance degrades over time.

**Maintenance Automation**: Automate test maintenance tasks like updating snapshots and cleaning up obsolete tests.

**Team Education**: Establish guidelines and training for writing performant tests and maintaining coverage quality.`,
      order: 4,
      estimatedTime: '1.5 hours',
      duration: '1.5 hours',
      difficulty: 'advanced',
      objectives: [
        'Optimize test execution speed',
        'Analyze and improve coverage',
        'Set up quality gates'
      ],
      codeExamples: [
        {
          title: "Jest Performance Configuration",
          code: `// jest.config.js - Optimized configuration
module.exports = {
  // Performance optimizations
  maxWorkers: '50%', // Use 50% of available CPU cores
  cache: true,
  cacheDirectory: '<rootDir>/node_modules/.cache/jest',
  
  // Coverage configuration
  collectCoverage: true,
  collectCoverageFrom: [
    'src/**/*.{js,jsx,ts,tsx}',
    '!src/**/*.d.ts',
    '!src/index.tsx',
    '!src/setupTests.ts',
    '!src/**/*.stories.{js,jsx,ts,tsx}',
    '!src/**/__tests__/**',
    '!src/**/test-utils/**'
  ],
  
  coverageThreshold: {
    global: {
      branches: 80,
      functions: 80,
      lines: 80,
      statements: 80
    },
    // Specific thresholds for critical modules
    './src/core/': {
      branches: 90,
      functions: 90,
      lines: 90,
      statements: 90
    }
  },
  
  // Transform optimization
  transform: {
    '^.+\\.(js|jsx|ts|tsx)$': ['babel-jest', {
      presets: [
        ['@babel/preset-env', { targets: { node: 'current' } }],
        '@babel/preset-react',
        '@babel/preset-typescript'
      ],
      cacheDirectory: true
    }]
  },
  
  // Module handling
  moduleNameMapping: {
    // Mock static assets to avoid loading them during tests
    '\\.(css|less|scss|sass)$': 'identity-obj-proxy',
    '\\.(jpg|jpeg|png|gif|eot|otf|webp|svg|ttf|woff|woff2)$': 
      '<rootDir>/src/__mocks__/fileMock.js'
  },
  
  // Test environment optimization
  testEnvironment: 'jsdom',
  setupFilesAfterEnv: ['<rootDir>/src/setupTests.ts'],
  
  // Reporter configuration for CI
  reporters: [
    'default',
    ['jest-junit', {
      outputDirectory: 'test-results',
      outputName: 'junit.xml'
    }],
    ['jest-html-reporter', {
      pageTitle: 'Test Report',
      outputPath: 'test-results/test-report.html'
    }]
  ]
};`,
          explanation: "Comprehensive Jest configuration optimized for performance and coverage",
          language: "javascript"
        },
        {
          title: "Test Performance Monitoring",
          code: `// testPerformanceMonitor.js
class TestPerformanceMonitor {
  constructor() {
    this.testTimes = new Map();
    this.slowTests = [];
    this.threshold = 1000; // 1 second threshold
  }

  startTest(testName) {
    this.testTimes.set(testName, Date.now());
  }

  endTest(testName) {
    const startTime = this.testTimes.get(testName);
    if (startTime) {
      const duration = Date.now() - startTime;
      
      if (duration > this.threshold) {
        this.slowTests.push({
          name: testName,
          duration,
          timestamp: new Date().toISOString()
        });
      }
      
      this.testTimes.delete(testName);
    }
  }

  generateReport() {
    if (this.slowTests.length > 0) {
      console.warn('🐌 Slow tests detected:');
      this.slowTests.forEach(test => {
        console.warn(\`  - \${test.name}: \${test.duration}ms\`);
      });
    }
    
    return {
      slowTestCount: this.slowTests.length,
      slowTests: this.slowTests
    };
  }
}

// setupTests.js - Global test performance monitoring
const performanceMonitor = new TestPerformanceMonitor();

beforeEach(() => {
  const testName = expect.getState().currentTestName;
  performanceMonitor.startTest(testName);
});

afterEach(() => {
  const testName = expect.getState().currentTestName;
  performanceMonitor.endTest(testName);
});

afterAll(() => {
  const report = performanceMonitor.generateReport();
  
  // Write performance report to file for CI analysis
  const fs = require('fs');
  fs.writeFileSync(
    'test-results/performance-report.json', 
    JSON.stringify(report, null, 2)
  );
});

// Coverage analysis helper
export const analyzeCoverage = (coverageMap) => {
  const summary = coverageMap.getCoverageSummary();
  
  const analysis = {
    overall: {
      lines: summary.lines.pct,
      branches: summary.branches.pct,
      functions: summary.functions.pct,
      statements: summary.statements.pct
    },
    uncoveredFiles: [],
    partiallyTestedFiles: []
  };

  coverageMap.files().forEach(filePath => {
    const fileCoverage = coverageMap.fileCoverageFor(filePath);
    const fileSummary = fileCoverage.toSummary();
    
    if (fileSummary.lines.pct < 50) {
      analysis.uncoveredFiles.push({
        file: filePath,
        coverage: fileSummary.lines.pct
      });
    } else if (fileSummary.lines.pct < 80) {
      analysis.partiallyTestedFiles.push({
        file: filePath,
        coverage: fileSummary.lines.pct
      });
    }
  });

  return analysis;
};`,
          explanation: "Performance monitoring and coverage analysis tools for test optimization",
          language: "javascript"
        }
      ],
      practice: {
        task: "Set up a comprehensive performance monitoring and coverage optimization system for a large React application test suite.",
        starter: `// Current test configuration needs optimization
// Slow test suite with coverage issues

// jest.config.js (needs optimization)
module.exports = {
  testEnvironment: 'jsdom',
  // Add performance optimizations here
};

// Create performance monitoring
// Set up coverage thresholds
// Implement test reporting`,
        solution: `// optimized-jest.config.js
const path = require('path');

module.exports = {
  // Performance optimizations
  maxWorkers: process.env.CI ? 2 : '50%',
  cache: true,
  cacheDirectory: '<rootDir>/node_modules/.cache/jest',
  clearMocks: true,
  restoreMocks: true,
  
  // Test environment
  testEnvironment: 'jsdom',
  setupFilesAfterEnv: [
    '<rootDir>/src/test-utils/setupTests.js',
    '<rootDir>/src/test-utils/performanceSetup.js'
  ],
  
  // Coverage configuration
  collectCoverage: true,
  collectCoverageFrom: [
    'src/**/*.{js,jsx,ts,tsx}',
    '!src/**/*.d.ts',
    '!src/**/*.stories.{js,jsx,ts,tsx}',
    '!src/**/__tests__/**',
    '!src/**/__mocks__/**',
    '!src/test-utils/**',
    '!src/index.tsx'
  ],
  
  coverageThreshold: {
    global: {
      branches: 75,
      functions: 75,
      lines: 75,
      statements: 75
    },
    './src/components/': {
      branches: 85,
      functions: 85,
      lines: 85,
      statements: 85
    },
    './src/hooks/': {
      branches: 90,
      functions: 90,
      lines: 90,
      statements: 90
    }
  },
  
  // Transform optimization
  transform: {
    '^.+\\.(js|jsx|ts|tsx)$': ['@swc/jest', {
      jsc: {
        parser: {
          syntax: 'typescript',
          tsx: true,
          decorators: false,
          dynamicImport: false
        },
        transform: {
          react: {
            runtime: 'automatic'
          }
        }
      }
    }]
  },
  
  // Module mapping for performance
  moduleNameMapping: {
    '^@/(.*)$': '<rootDir>/src/$1',
    '\\.(css|less|scss|sass)$': 'identity-obj-proxy',
    '\\.(jpg|jpeg|png|gif|eot|otf|webp|svg|ttf|woff|woff2)$': 
      '<rootDir>/src/__mocks__/fileMock.js'
  },
  
  // Ignore large dependencies during tests
  transformIgnorePatterns: [
    'node_modules/(?!(react-markdown|remark-gfm|unified)/)'
  ],
  
  // Test reporting
  reporters: [
    'default',
    ['jest-junit', {
      outputDirectory: 'coverage',
      outputName: 'junit.xml'
    }],
    ['./src/test-utils/performanceReporter.js', {}]
  ],
  
  // Global configuration
  globals: {
    'ts-jest': {
      useESM: true
    }
  }
};

// src/test-utils/performanceSetup.js
import { performance } from 'perf_hooks';

class TestPerformanceTracker {
  constructor() {
    this.testMetrics = [];
    this.suiteTimes = new Map();
    this.testTimes = new Map();
  }

  startSuite(suiteName) {
    this.suiteTimes.set(suiteName, performance.now());
  }

  endSuite(suiteName) {
    const startTime = this.suiteTimes.get(suiteName);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.suiteTimes.delete(suiteName);
      return duration;
    }
  }

  startTest(testName) {
    this.testTimes.set(testName, performance.now());
  }

  endTest(testName, status) {
    const startTime = this.testTimes.get(testName);
    if (startTime) {
      const duration = performance.now() - startTime;
      this.testMetrics.push({
        name: testName,
        duration,
        status,
        timestamp: new Date().toISOString()
      });
      this.testTimes.delete(testName);
    }
  }

  getSlowTests(threshold = 1000) {
    return this.testMetrics.filter(test => test.duration > threshold);
  }

  generateReport() {
    const slowTests = this.getSlowTests();
    const averageTime = this.testMetrics.reduce((sum, test) => 
      sum + test.duration, 0) / this.testMetrics.length;

    return {
      totalTests: this.testMetrics.length,
      averageTime: Math.round(averageTime),
      slowTests: slowTests.length,
      slowTestDetails: slowTests.map(test => ({
        name: test.name,
        duration: Math.round(test.duration)
      }))
    };
  }
}

const tracker = new TestPerformanceTracker();

beforeAll(() => {
  tracker.startSuite(expect.getState().testPath);
});

afterAll(() => {
  const report = tracker.generateReport();
  
  if (report.slowTests > 0) {
    console.warn(\`⚠️  Found \${report.slowTests} slow tests:\`);
    report.slowTestDetails.forEach(test => {
      console.warn(\`   \${test.name}: \${test.duration}ms\`);
    });
  }
  
  // Write performance data for CI analysis
  const fs = require('fs');
  const path = require('path');
  
  const reportDir = path.join(process.cwd(), 'coverage');
  if (!fs.existsSync(reportDir)) {
    fs.mkdirSync(reportDir, { recursive: true });
  }
  
  fs.writeFileSync(
    path.join(reportDir, 'performance.json'),
    JSON.stringify(report, null, 2)
  );
});

beforeEach(() => {
  const testName = expect.getState().currentTestName;
  tracker.startTest(testName);
});

afterEach(() => {
  const testName = expect.getState().currentTestName;
  const testResult = expect.getState();
  tracker.endTest(testName, testResult.passed ? 'passed' : 'failed');
});

// src/test-utils/performanceReporter.js
class PerformanceReporter {
  constructor(globalConfig, options) {
    this.globalConfig = globalConfig;
    this.options = options;
  }

  onRunComplete(contexts, results) {
    const fs = require('fs');
    const path = require('path');

    const performanceData = {
      totalTime: results.testResults.reduce((total, result) => 
        total + (result.perfStats.end - result.perfStats.start), 0),
      numTotalTests: results.numTotalTests,
      numPassedTests: results.numPassedTests,
      numFailedTests: results.numFailedTests,
      testResults: results.testResults.map(result => ({
        testFilePath: result.testFilePath,
        duration: result.perfStats.end - result.perfStats.start,
        numTests: result.numPassingTests + result.numFailingTests
      }))
    };

    // Identify slowest test files
    const slowestFiles = performanceData.testResults
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 10);

    const report = {
      summary: {
        totalExecutionTime: \`\${Math.round(performanceData.totalTime)}ms\`,
        averageTestTime: \`\${Math.round(performanceData.totalTime / performanceData.numTotalTests)}ms\`,
        testsPerSecond: Math.round(performanceData.numTotalTests / (performanceData.totalTime / 1000))
      },
      slowestFiles: slowestFiles.map(file => ({
        file: path.relative(process.cwd(), file.testFilePath),
        duration: \`\${Math.round(file.duration)}ms\`,
        testsCount: file.numTests
      }))
    };

    console.log('\\n📊 Test Performance Report:');
    console.log(\`   Total time: \${report.summary.totalExecutionTime}\`);
    console.log(\`   Average per test: \${report.summary.averageTestTime}\`);
    console.log(\`   Tests per second: \${report.summary.testsPerSecond}\`);

    if (slowestFiles.length > 0) {
      console.log('\\n🐌 Slowest test files:');
      report.slowestFiles.forEach((file, index) => {
        console.log(\`   \${index + 1}. \${file.file} - \${file.duration}\`);
      });
    }

    // Save detailed report
    const reportPath = path.join(this.globalConfig.coverageDirectory || 'coverage', 'performance-detailed.json');
    fs.writeFileSync(reportPath, JSON.stringify(report, null, 2));
  }
}

module.exports = PerformanceReporter;`,
        hints: [
          "Use SWC or esbuild for faster transpilation instead of Babel",
          "Configure maxWorkers based on environment (CI vs local)",
          "Mock static assets and large dependencies to reduce load time",
          "Set up performance monitoring in test setup files",
          "Create custom reporters for detailed performance analysis",
          "Use coverage thresholds strategically, not just for 100% coverage",
          "Track performance metrics over time to catch regressions"
        ]
      }
    }
  ]
};

export const playwrightCourse: Course = {
  id: 'playwright-e2e-testing',
  title: 'End-to-End Testing with Playwright',
  description: 'Modern E2E testing with Playwright for reliable web application testing.',
  level: 'Intermediate',
  totalDuration: '8 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['JavaScript/TypeScript', 'Web development basics', 'Testing concepts'],
  learningOutcomes: [
    'Write reliable E2E tests with Playwright',
    'Implement page object patterns',
    'Handle complex user interactions',
    'Set up CI/CD testing pipelines'
  ],
  modules: [
    {
      id: 'playwright-fundamentals',
      title: 'Playwright Fundamentals',
      description: 'Getting started with Playwright testing framework',
      content: `## Playwright Fundamentals

Playwright is a modern end-to-end testing framework that enables reliable testing across multiple browsers. It provides a simple yet powerful API for automating web browsers and testing web applications.

### Introduction to Playwright

**Cross-Browser Testing**: Playwright supports testing on Chromium, Firefox, and WebKit, ensuring your applications work across all major browsers.

**Modern Web Features**: Built with modern web applications in mind, supporting features like single-page applications, progressive web apps, and complex user interactions.

**Auto-Wait Functionality**: Playwright automatically waits for elements to be ready before performing actions, reducing flaky tests caused by timing issues.

**Network Interception**: Intercept and modify network requests to test different scenarios and mock backend responses.

### Setting Up Playwright

**Installation**: Install Playwright using npm and configure it for your project with the appropriate browsers and testing configuration.

**Configuration**: Set up playwright.config.js to define browser settings, test directories, and execution parameters.

**Test Structure**: Understand the basic structure of Playwright tests and how to organize test files and test cases.

**Browser Context**: Learn about browser contexts and how they provide isolated environments for each test.

### Playwright Selectors

**Text Selectors**: Find elements by their visible text content, making tests more readable and maintainable.

**CSS Selectors**: Use standard CSS selectors to locate elements by class, ID, attributes, and element structure.

**XPath Selectors**: Leverage XPath when CSS selectors are insufficient for complex element relationships.

**Built-in Locators**: Use Playwright's built-in locators like getByRole, getByLabel, and getByTestId for semantic element selection.

### Basic Test Operations

**Page Navigation**: Navigate to pages, handle redirects, and wait for page load completion.

**Element Interactions**: Click buttons, fill forms, select options, and perform keyboard and mouse interactions.

**Assertions**: Verify page content, element states, and application behavior using Playwright's assertion library.

**Screenshots and Videos**: Capture visual evidence of test execution for debugging and documentation purposes.

### Browser and Context Management

**Browser Lifecycle**: Understand when to create new browser instances versus reusing existing ones.

**Context Isolation**: Use browser contexts to isolate tests and prevent state leakage between test cases.

**Page Management**: Handle multiple pages and tabs within a single test scenario.

**Mobile Testing**: Configure Playwright to simulate mobile devices and test responsive designs.

### Best Practices for Beginners

**Test Independence**: Design tests to run independently without relying on previous test state.

**Reliable Selectors**: Choose selectors that are stable and unlikely to break with UI changes.

**Explicit Waits**: Use appropriate wait strategies for dynamic content and asynchronous operations.

**Error Handling**: Implement proper error handling and meaningful test failure messages.`,
      order: 1,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'beginner',
      objectives: [
        'Set up Playwright testing environment',
        'Write basic E2E tests',
        'Use Playwright selectors effectively'
      ],
      codeExamples: [
        {
          title: "Basic Playwright Test Setup",
          code: `// playwright.config.js
import { defineConfig, devices } from '@playwright/test';

export default defineConfig({
  testDir: './tests',
  fullyParallel: true,
  forbidOnly: !!process.env.CI,
  retries: process.env.CI ? 2 : 0,
  workers: process.env.CI ? 1 : undefined,
  reporter: 'html',
  
  use: {
    baseURL: 'http://localhost:3000',
    trace: 'on-first-retry',
    screenshot: 'only-on-failure',
  },

  projects: [
    {
      name: 'chromium',
      use: { ...devices['Desktop Chrome'] },
    },
    {
      name: 'firefox',
      use: { ...devices['Desktop Firefox'] },
    },
    {
      name: 'webkit',
      use: { ...devices['Desktop Safari'] },
    },
  ],

  webServer: {
    command: 'npm run start',
    url: 'http://localhost:3000',
    reuseExistingServer: !process.env.CI,
  },
});

// tests/example.spec.js
import { test, expect } from '@playwright/test';

test('basic navigation test', async ({ page }) => {
  await page.goto('/');
  
  // Wait for page to load
  await expect(page).toHaveTitle(/My App/);
  
  // Check main content is visible
  await expect(page.getByRole('main')).toBeVisible();
  
  // Navigate to about page
  await page.getByRole('link', { name: 'About' }).click();
  await expect(page).toHaveURL('/about');
});`,
          explanation: "Basic Playwright configuration and a simple navigation test",
          language: "javascript"
        },
        {
          title: "Playwright Selectors and Interactions",
          code: `import { test, expect } from '@playwright/test';

test('form interaction test', async ({ page }) => {
  await page.goto('/contact');

  // Using built-in locators (recommended)
  await page.getByLabel('Name').fill('John Doe');
  await page.getByLabel('Email').fill('john@example.com');
  await page.getByRole('combobox', { name: 'Country' }).selectOption('US');
  
  // Using text content
  await page.getByText('Subscribe to newsletter').check();
  
  // Using test IDs (good for testing)
  await page.getByTestId('submit-button').click();
  
  // Assertions with auto-waiting
  await expect(page.getByText('Thank you for your message')).toBeVisible();
  
  // Check URL changed
  await expect(page).toHaveURL(/success/);
});

test('multiple selector strategies', async ({ page }) => {
  await page.goto('/products');

  // CSS selectors
  await page.locator('.product-card').first().click();
  
  // XPath for complex relationships
  await page.locator('//button[contains(text(), "Add to Cart")]').click();
  
  // Chaining locators
  await page
    .locator('.shopping-cart')
    .getByRole('button', { name: 'Checkout' })
    .click();
    
  // Using filters
  await page
    .getByRole('listitem')
    .filter({ hasText: 'Product Name' })
    .getByRole('button')
    .click();
});`,
          explanation: "Demonstrates various Playwright selector strategies and form interactions",
          language: "javascript"
        }
      ],
      practice: {
        task: "Create a comprehensive test suite for an e-commerce product page that tests navigation, product selection, and cart functionality.",
        starter: `// tests/ecommerce.spec.js
import { test, expect } from '@playwright/test';

test.describe('E-commerce Product Tests', () => {
  // Write tests for:
  // 1. Product page navigation
  // 2. Product image gallery
  // 3. Add to cart functionality
  // 4. Quantity selection
  // 5. Product reviews section
});`,
        solution: `import { test, expect } from '@playwright/test';

test.describe('E-commerce Product Tests', () => {
  test.beforeEach(async ({ page }) => {
    await page.goto('/products');
  });

  test('should navigate to product details', async ({ page }) => {
    // Click on first product
    await page.getByTestId('product-card').first().click();
    
    // Verify we're on product details page
    await expect(page).toHaveURL(/\\/products\\/\\d+/);
    await expect(page.getByRole('heading', { level: 1 })).toBeVisible();
    await expect(page.getByTestId('product-price')).toBeVisible();
  });

  test('should interact with product image gallery', async ({ page }) => {
    await page.getByTestId('product-card').first().click();
    
    // Check main image is visible
    await expect(page.getByTestId('main-product-image')).toBeVisible();
    
    // Click on thumbnail to change main image
    await page.getByTestId('image-thumbnail').nth(1).click();
    
    // Verify image source changed
    const mainImage = page.getByTestId('main-product-image');
    await expect(mainImage).toHaveAttribute('src', /image-2/);
    
    // Test zoom functionality
    await mainImage.hover();
    await expect(page.getByTestId('image-zoom')).toBeVisible();
  });

  test('should add product to cart', async ({ page }) => {
    await page.getByTestId('product-card').first().click();
    
    // Select size if available
    const sizeSelector = page.getByTestId('size-selector');
    if (await sizeSelector.isVisible()) {
      await sizeSelector.selectOption('M');
    }
    
    // Select color if available
    const colorSelector = page.getByTestId('color-selector');
    if (await colorSelector.isVisible()) {
      await colorSelector.first().click();
    }
    
    // Change quantity
    await page.getByTestId('quantity-input').fill('2');
    
    // Add to cart
    await page.getByRole('button', { name: 'Add to Cart' }).click();
    
    // Verify success message
    await expect(page.getByText('Added to cart')).toBeVisible();
    
    // Check cart count updated
    await expect(page.getByTestId('cart-count')).toHaveText('2');
  });

  test('should handle quantity changes', async ({ page }) => {
    await page.getByTestId('product-card').first().click();
    
    const quantityInput = page.getByTestId('quantity-input');
    const priceElement = page.getByTestId('total-price');
    
    // Get initial price
    const initialPrice = await priceElement.textContent();
    
    // Increase quantity using buttons
    await page.getByTestId('quantity-increase').click();
    await expect(quantityInput).toHaveValue('2');
    
    // Verify price updated
    await expect(priceElement).not.toHaveText(initialPrice);
    
    // Test minimum quantity
    await page.getByTestId('quantity-decrease').click();
    await page.getByTestId('quantity-decrease').click();
    await expect(quantityInput).toHaveValue('1');
    
    // Test maximum quantity if applicable
    await quantityInput.fill('999');
    await quantityInput.blur();
    // Should handle max quantity gracefully
  });

  test('should display and interact with reviews', async ({ page }) => {
    await page.getByTestId('product-card').first().click();
    
    // Scroll to reviews section
    await page.getByTestId('reviews-section').scrollIntoViewIfNeeded();
    
    // Check reviews are visible
    await expect(page.getByTestId('review-item').first()).toBeVisible();
    
    // Test pagination if available
    const nextPageButton = page.getByRole('button', { name: 'Next' });
    if (await nextPageButton.isVisible()) {
      await nextPageButton.click();
      await expect(page.getByTestId('review-item').first()).toBeVisible();
    }
    
    // Test review sorting
    const sortSelect = page.getByTestId('review-sort');
    if (await sortSelect.isVisible()) {
      await sortSelect.selectOption('newest');
      await page.waitForLoadState('networkidle');
      await expect(page.getByTestId('review-item').first()).toBeVisible();
    }
    
    // Test helpful votes
    const helpfulButton = page.getByTestId('review-helpful').first();
    if (await helpfulButton.isVisible()) {
      await helpfulButton.click();
      await expect(helpfulButton).toHaveText(/Helpful \\(\\d+\\)/);
    }
  });
});`,
        hints: [
          "Use getByTestId for elements that need reliable selection",
          "Always use built-in locators like getByRole when possible",
          "Check for element visibility before interacting with optional elements",
          "Use waitForLoadState when content loads dynamically",
          "Group related tests using test.describe blocks",
          "Use test.beforeEach for common setup across tests"
        ]
      }
    },
    {
      id: 'playwright-page-objects',
      title: 'Page Object Model',
      description: 'Implementing maintainable test architecture with page objects',
      content: `## Page Object Model with Playwright

The Page Object Model (POM) is a design pattern that creates an abstraction layer between your tests and the web pages they interact with. This pattern improves test maintainability, reduces code duplication, and makes tests more readable.

### Understanding Page Object Pattern

**Separation of Concerns**: Page objects encapsulate the structure and behavior of web pages, separating the "what" (test logic) from the "how" (page interactions).

**Maintainability**: When UI elements change, you only need to update the page object rather than every test that uses those elements.

**Reusability**: Page objects can be shared across multiple tests, reducing code duplication and ensuring consistency.

**Readability**: Tests become more readable by using domain-specific language rather than technical selector details.

### Designing Page Objects

**Element Encapsulation**: Store element selectors and interaction methods within page objects to hide implementation details from tests.

**Action Methods**: Create methods that represent user actions on the page, such as login(), addToCart(), or submitForm().

**Assertion Methods**: Include methods that verify page state and content, making tests more expressive and maintainable.

**Page Navigation**: Handle page navigation and ensure page objects represent the current state of the application.

### Playwright Page Object Implementation

**Base Page Class**: Create a base page class that contains common functionality shared across all page objects.

**Page-Specific Classes**: Implement specific page classes that extend the base class and define page-unique elements and behaviors.

**Fixture Integration**: Integrate page objects with Playwright's fixture system for dependency injection and lifecycle management.

**Type Safety**: Use TypeScript to provide type safety and better IDE support for page object methods and properties.

### Advanced Page Object Patterns

**Page Component Objects**: Break down complex pages into smaller component objects that represent specific UI sections.

**Page Factory Pattern**: Use factory methods to create page objects based on current page state or URL patterns.

**Fluent Interface**: Design page object methods to return the appropriate page object, enabling method chaining for complex workflows.

**Data-Driven Page Objects**: Create page objects that can work with different data sets and configurations.

### Managing Page Object Hierarchy

**Inheritance Structure**: Design an inheritance hierarchy that reflects the structure of your application's pages and components.

**Composition over Inheritance**: Use composition to combine smaller page components into larger page objects when appropriate.

**Shared Components**: Create reusable component objects for elements that appear across multiple pages, such as navigation bars or modal dialogs.

**Page State Management**: Handle different page states and conditions within page objects to make them more robust.

### Best Practices for Page Objects

**Single Responsibility**: Each page object should represent a single page or logical grouping of functionality.

**Wait Strategies**: Implement appropriate wait strategies within page objects to handle dynamic content and loading states.

**Error Handling**: Add proper error handling and meaningful error messages to page object methods.

**Documentation**: Document page object methods and their expected behavior to help other team members understand the API.`,
      order: 2,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'intermediate',
      objectives: [
        'Design page object models',
        'Implement reusable test components',
        'Structure test suites effectively'
      ],
      codeExamples: [
        {
          title: "Base Page Object Implementation",
          code: `// pages/BasePage.js
export class BasePage {
  constructor(page) {
    this.page = page;
  }

  async goto(url) {
    await this.page.goto(url);
    await this.waitForPageLoad();
  }

  async waitForPageLoad() {
    await this.page.waitForLoadState('networkidle');
  }

  async takeScreenshot(name) {
    await this.page.screenshot({ path: \`screenshots/\${name}.png\` });
  }

  async waitForElement(selector, options = {}) {
    return await this.page.waitForSelector(selector, options);
  }

  // Common navigation methods
  async clickLink(text) {
    await this.page.getByRole('link', { name: text }).click();
  }

  async fillForm(formData) {
    for (const [field, value] of Object.entries(formData)) {
      await this.page.getByLabel(field).fill(value);
    }
  }
}

// pages/LoginPage.js
import { BasePage } from './BasePage.js';

export class LoginPage extends BasePage {
  constructor(page) {
    super(page);
    
    // Define page elements
    this.emailInput = page.getByLabel('Email');
    this.passwordInput = page.getByLabel('Password');
    this.loginButton = page.getByRole('button', { name: 'Login' });
    this.errorMessage = page.getByTestId('error-message');
    this.forgotPasswordLink = page.getByRole('link', { name: 'Forgot Password' });
  }

  async login(email, password) {
    await this.emailInput.fill(email);
    await this.passwordInput.fill(password);
    await this.loginButton.click();
    await this.waitForNavigation();
  }

  async loginWithValidUser() {
    await this.login('user@example.com', 'password123');
  }

  async expectLoginError(expectedMessage) {
    await expect(this.errorMessage).toBeVisible();
    await expect(this.errorMessage).toHaveText(expectedMessage);
  }

  async goToForgotPassword() {
    await this.forgotPasswordLink.click();
    return new ForgotPasswordPage(this.page);
  }

  async waitForNavigation() {
    await this.page.waitForURL('/dashboard');
  }

  async isDisplayed() {
    await expect(this.loginButton).toBeVisible();
    await expect(this.emailInput).toBeVisible();
    await expect(this.passwordInput).toBeVisible();
  }
}`,
          explanation: "Base page class and specific login page implementation with common patterns",
          language: "javascript"
        },
        {
          title: "Component-Based Page Objects",
          code: `// components/NavigationComponent.js
export class NavigationComponent {
  constructor(page) {
    this.page = page;
    this.navbar = page.getByRole('navigation');
    this.userMenu = page.getByTestId('user-menu');
    this.cartIcon = page.getByTestId('cart-icon');
    this.searchBox = page.getByPlaceholder('Search products');
  }

  async navigateTo(section) {
    await this.navbar.getByRole('link', { name: section }).click();
  }

  async openUserMenu() {
    await this.userMenu.click();
    return new UserMenuComponent(this.page);
  }

  async search(query) {
    await this.searchBox.fill(query);
    await this.searchBox.press('Enter');
    await this.page.waitForURL('**/search**');
  }

  async getCartItemCount() {
    const cartBadge = this.cartIcon.locator('.badge');
    if (await cartBadge.isVisible()) {
      return await cartBadge.textContent();
    }
    return '0';
  }
}

// pages/ProductPage.js
import { BasePage } from './BasePage.js';
import { NavigationComponent } from '../components/NavigationComponent.js';

export class ProductPage extends BasePage {
  constructor(page) {
    super(page);
    
    // Composition - include navigation component
    this.navigation = new NavigationComponent(page);
    
    // Product-specific elements
    this.productTitle = page.getByTestId('product-title');
    this.productPrice = page.getByTestId('product-price');
    this.addToCartButton = page.getByRole('button', { name: 'Add to Cart' });
    this.quantitySelector = page.getByTestId('quantity-selector');
    this.sizeSelector = page.getByTestId('size-selector');
    this.imageGallery = page.getByTestId('image-gallery');
    this.reviewsSection = page.getByTestId('reviews-section');
  }

  async addToCart(options = {}) {
    if (options.size) {
      await this.selectSize(options.size);
    }
    
    if (options.quantity) {
      await this.setQuantity(options.quantity);
    }
    
    await this.addToCartButton.click();
    await this.waitForCartUpdate();
    
    return this; // Enable method chaining
  }

  async selectSize(size) {
    await this.sizeSelector.selectOption(size);
    return this;
  }

  async setQuantity(quantity) {
    await this.quantitySelector.fill(quantity.toString());
    return this;
  }

  async waitForCartUpdate() {
    // Wait for cart count to update
    await this.page.waitForFunction(() => {
      const cartBadge = document.querySelector('[data-testid="cart-icon"] .badge');
      return cartBadge && parseInt(cartBadge.textContent) > 0;
    });
  }

  async getProductInfo() {
    return {
      title: await this.productTitle.textContent(),
      price: await this.productPrice.textContent(),
      availability: await this.isInStock()
    };
  }

  async isInStock() {
    return await this.addToCartButton.isEnabled();
  }

  async viewReviews() {
    await this.reviewsSection.scrollIntoViewIfNeeded();
    return new ReviewsComponent(this.page);
  }
}`,
          explanation: "Component-based page objects with composition and method chaining",
          language: "javascript"
        }
      ],
      practice: {
        task: "Design a complete page object architecture for an e-commerce application with login, product catalog, shopping cart, and checkout pages.",
        starter: `// Design page objects for:
// 1. LoginPage
// 2. ProductCatalogPage  
// 3. ProductDetailsPage
// 4. ShoppingCartPage
// 5. CheckoutPage
// 6. Shared components (Navigation, Footer, etc.)

// Create base classes and implement the page object pattern`,
        solution: `// pages/BasePage.js
export class BasePage {
  constructor(page) {
    this.page = page;
    this.navigation = new NavigationComponent(page);
    this.footer = new FooterComponent(page);
  }

  async goto(url) {
    await this.page.goto(url);
    await this.waitForPageLoad();
  }

  async waitForPageLoad() {
    await this.page.waitForLoadState('networkidle');
  }

  async expectPageTitle(title) {
    await expect(this.page).toHaveTitle(title);
  }

  async handleAlert(action = 'accept') {
    this.page.on('dialog', dialog => dialog[action]());
  }
}

// components/NavigationComponent.js
export class NavigationComponent {
  constructor(page) {
    this.page = page;
    this.logo = page.getByTestId('logo');
    this.searchBox = page.getByPlaceholder('Search');
    this.cartIcon = page.getByTestId('cart-icon');
    this.userMenu = page.getByTestId('user-menu');
    this.categoriesMenu = page.getByTestId('categories-menu');
  }

  async search(query) {
    await this.searchBox.fill(query);
    await this.searchBox.press('Enter');
  }

  async goToCategory(category) {
    await this.categoriesMenu.hover();
    await this.page.getByRole('link', { name: category }).click();
  }

  async goToCart() {
    await this.cartIcon.click();
    return new ShoppingCartPage(this.page);
  }

  async logout() {
    await this.userMenu.click();
    await this.page.getByRole('button', { name: 'Logout' }).click();
    return new LoginPage(this.page);
  }
}

// pages/LoginPage.js
export class LoginPage extends BasePage {
  constructor(page) {
    super(page);
    this.emailInput = page.getByLabel('Email');
    this.passwordInput = page.getByLabel('Password');
    this.loginButton = page.getByRole('button', { name: 'Sign In' });
    this.signupLink = page.getByRole('link', { name: 'Create Account' });
    this.errorAlert = page.getByTestId('error-alert');
  }

  async login(email, password) {
    await this.emailInput.fill(email);
    await this.passwordInput.fill(password);
    await this.loginButton.click();
    await this.page.waitForURL('/dashboard');
    return new DashboardPage(this.page);
  }

  async expectLoginError(message) {
    await expect(this.errorAlert).toBeVisible();
    await expect(this.errorAlert).toContainText(message);
  }

  async goToSignup() {
    await this.signupLink.click();
    return new SignupPage(this.page);
  }
}

// pages/ProductCatalogPage.js
export class ProductCatalogPage extends BasePage {
  constructor(page) {
    super(page);
    this.productGrid = page.getByTestId('product-grid');
    this.filterSidebar = page.getByTestId('filters-sidebar');
    this.sortDropdown = page.getByTestId('sort-dropdown');
    this.paginationComponent = new PaginationComponent(page);
  }

  async getProductCards() {
    return this.productGrid.getByTestId('product-card').all();
  }

  async selectProduct(index = 0) {
    const productCards = await this.getProductCards();
    await productCards[index].click();
    return new ProductDetailsPage(this.page);
  }

  async filterByPrice(min, max) {
    await this.filterSidebar.getByLabel('Min Price').fill(min.toString());
    await this.filterSidebar.getByLabel('Max Price').fill(max.toString());
    await this.filterSidebar.getByRole('button', { name: 'Apply Filters' }).click();
    await this.waitForFilterResults();
  }

  async filterByCategory(category) {
    await this.filterSidebar.getByLabel(category).check();
    await this.waitForFilterResults();
  }

  async sortBy(option) {
    await this.sortDropdown.selectOption(option);
    await this.waitForFilterResults();
  }

  async waitForFilterResults() {
    await this.page.waitForLoadState('networkidle');
    await expect(this.productGrid).toBeVisible();
  }

  async getProductCount() {
    const products = await this.getProductCards();
    return products.length;
  }
}

// pages/ProductDetailsPage.js
export class ProductDetailsPage extends BasePage {
  constructor(page) {
    super(page);
    this.productTitle = page.getByTestId('product-title');
    this.productPrice = page.getByTestId('product-price');
    this.productImage = page.getByTestId('product-image');
    this.addToCartButton = page.getByRole('button', { name: 'Add to Cart' });
    this.quantityInput = page.getByTestId('quantity-input');
    this.sizeSelector = page.getByTestId('size-selector');
    this.colorSelector = page.getByTestId('color-selector');
    this.reviewsTab = page.getByRole('tab', { name: 'Reviews' });
    this.specificationsTab = page.getByRole('tab', { name: 'Specifications' });
  }

  async addToCart(options = {}) {
    if (options.size) await this.selectSize(options.size);
    if (options.color) await this.selectColor(options.color);
    if (options.quantity) await this.setQuantity(options.quantity);
    
    await this.addToCartButton.click();
    await this.expectAddToCartSuccess();
    return this;
  }

  async selectSize(size) {
    await this.sizeSelector.selectOption(size);
  }

  async selectColor(color) {
    await this.colorSelector.getByTitle(color).click();
  }

  async setQuantity(quantity) {
    await this.quantityInput.fill(quantity.toString());
  }

  async expectAddToCartSuccess() {
    await expect(this.page.getByText('Added to cart')).toBeVisible();
  }

  async viewReviews() {
    await this.reviewsTab.click();
    return new ReviewsComponent(this.page);
  }

  async getProductDetails() {
    return {
      title: await this.productTitle.textContent(),
      price: await this.productPrice.textContent(),
      inStock: await this.addToCartButton.isEnabled()
    };
  }
}

// pages/ShoppingCartPage.js
export class ShoppingCartPage extends BasePage {
  constructor(page) {
    super(page);
    this.cartItems = page.getByTestId('cart-items');
    this.subtotalAmount = page.getByTestId('subtotal-amount');
    this.checkoutButton = page.getByRole('button', { name: 'Proceed to Checkout' });
    this.continueShoppingLink = page.getByRole('link', { name: 'Continue Shopping' });
    this.emptyCartMessage = page.getByTestId('empty-cart-message');
  }

  async getCartItems() {
    return this.cartItems.getByTestId('cart-item').all();
  }

  async removeItem(index = 0) {
    const items = await this.getCartItems();
    await items[index].getByRole('button', { name: 'Remove' }).click();
    await this.confirmRemoval();
  }

  async updateQuantity(index, quantity) {
    const items = await this.getCartItems();
    await items[index].getByTestId('quantity-input').fill(quantity.toString());
    await items[index].getByRole('button', { name: 'Update' }).click();
  }

  async confirmRemoval() {
    await this.page.getByRole('button', { name: 'Yes, Remove' }).click();
  }

  async proceedToCheckout() {
    await this.checkoutButton.click();
    return new CheckoutPage(this.page);
  }

  async expectEmptyCart() {
    await expect(this.emptyCartMessage).toBeVisible();
    await expect(this.checkoutButton).not.toBeVisible();
  }

  async getSubtotal() {
    return await this.subtotalAmount.textContent();
  }
}

// pages/CheckoutPage.js
export class CheckoutPage extends BasePage {
  constructor(page) {
    super(page);
    this.shippingForm = new ShippingFormComponent(page);
    this.paymentForm = new PaymentFormComponent(page);
    this.orderSummary = new OrderSummaryComponent(page);
    this.placeOrderButton = page.getByRole('button', { name: 'Place Order' });
  }

  async fillShippingInfo(shippingData) {
    await this.shippingForm.fill(shippingData);
    return this;
  }

  async fillPaymentInfo(paymentData) {
    await this.paymentForm.fill(paymentData);
    return this;
  }

  async placeOrder() {
    await this.placeOrderButton.click();
    await this.page.waitForURL('**/order-confirmation**');
    return new OrderConfirmationPage(this.page);
  }

  async getOrderTotal() {
    return await this.orderSummary.getTotal();
  }
}

// Test example using page objects
test('complete e-commerce flow', async ({ page }) => {
  const loginPage = new LoginPage(page);
  await loginPage.goto('/login');
  
  const catalogPage = await loginPage.login('user@example.com', 'password');
  await catalogPage.navigation.goToCategory('Electronics');
  
  const productPage = await catalogPage.selectProduct(0);
  await productPage.addToCart({ size: 'M', quantity: 2 });
  
  const cartPage = await productPage.navigation.goToCart();
  const checkoutPage = await cartPage.proceedToCheckout();
  
  await checkoutPage
    .fillShippingInfo({ name: 'John Doe', address: '123 Main St' })
    .fillPaymentInfo({ cardNumber: '4111111111111111' });
    
  const confirmationPage = await checkoutPage.placeOrder();
  await confirmationPage.expectOrderSuccess();
});`,
        hints: [
          "Create a base page class with common functionality",
          "Use composition for shared components like navigation",
          "Implement method chaining for fluent interfaces",
          "Separate page elements from actions for better maintainability",
          "Use factory methods to return appropriate page objects",
          "Include wait strategies in page object methods",
          "Create component objects for reusable UI elements"
        ]
      }
    },
    {
      id: 'playwright-advanced-interactions',
      title: 'Advanced User Interactions',
      description: 'Testing complex user workflows and interactions',
      content: `## Advanced User Interactions with Playwright

Modern web applications require testing of complex user interactions including file operations, authentication flows, drag-and-drop, keyboard shortcuts, and multi-step workflows.

### File Upload and Download Testing

**File Upload Workflows**: Test file selection, validation, progress indicators, and upload completion with various file types and sizes.

**Download Verification**: Verify file downloads, check file content, and test download progress and cancellation.

**Drag and Drop Operations**: Test drag-and-drop functionality for file uploads, list reordering, and UI element positioning.

### Authentication and Session Management

**Login Flows**: Test various authentication methods including social login, multi-factor authentication, and single sign-on.

**Session Persistence**: Verify session management, automatic logout, and session restoration across browser restarts.

**Authorization Testing**: Test role-based access control and permission-based UI visibility.

### Complex User Workflows

**Multi-Step Processes**: Test wizards, checkout flows, and form sequences with proper state management.

**Keyboard Navigation**: Verify accessibility through keyboard-only navigation and shortcuts.

**Mobile Interactions**: Test touch gestures, swipe actions, and responsive behavior.`,
      order: 3,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'intermediate',
      objectives: [
        'Test file upload/download flows',
        'Handle authentication states',
        'Test complex user interactions'
      ],
      codeExamples: [
        {
          title: "File Upload and Download Testing",
          code: `test('file upload and download', async ({ page }) => {
  // File upload
  await page.goto('/upload');
  const fileInput = page.getByTestId('file-input');
  await fileInput.setInputFiles('test-files/sample.pdf');
  await page.getByRole('button', { name: 'Upload' }).click();
  await expect(page.getByText('File uploaded successfully')).toBeVisible();
  
  // File download
  const downloadPromise = page.waitForDownload();
  await page.getByRole('button', { name: 'Download' }).click();
  const download = await downloadPromise;
  
  // Verify download
  expect(download.suggestedFilename()).toBe('report.pdf');
  await download.saveAs('downloads/report.pdf');
});`,
          explanation: "Testing file upload and download workflows",
          language: "javascript"
        }
      ]
    },
    {
      id: 'playwright-cicd-integration',
      title: 'CI/CD Integration',
      description: 'Integrating Playwright tests in CI/CD pipelines',
      content: `## CI/CD Integration with Playwright

Integrating Playwright tests into CI/CD pipelines ensures consistent quality gates and automated testing across development workflows.

### Pipeline Configuration

**GitHub Actions Setup**: Configure Playwright tests to run on pull requests, pushes, and scheduled intervals with proper browser installation.

**Docker Integration**: Use Playwright Docker images for consistent testing environments across different CI platforms.

**Parallel Execution**: Configure test sharding and parallel execution to reduce CI feedback time.

### Test Reporting and Artifacts

**HTML Reports**: Generate comprehensive HTML reports with test results, screenshots, and trace files.

**Artifact Management**: Store test artifacts including videos, screenshots, and trace files for debugging failed tests.

**Test Analytics**: Track test performance, flaky tests, and failure patterns over time.

### Environment Management

**Test Data**: Manage test data and database states for consistent test execution.

**Service Dependencies**: Handle external service dependencies and API mocking in CI environments.

**Security**: Secure handling of credentials and sensitive test data in CI pipelines.`,
      order: 4,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Configure CI/CD test pipelines',
        'Set up parallel test execution',
        'Generate comprehensive test reports'
      ]
    }
  ]
};

export const performanceTestingCourse: Course = {
  id: 'performance-testing-comprehensive',
  title: 'Comprehensive Performance Testing',
  description: 'Master performance testing strategies, tools, and optimization techniques.',
  level: 'Advanced',
  totalDuration: '8 hours',
  image: '/api/placeholder/400/300',
  prerequisites: ['Web development', 'Testing fundamentals', 'Basic performance concepts'],
  learningOutcomes: [
    'Design performance testing strategies',
    'Use multiple performance testing tools',
    'Analyze performance bottlenecks',
    'Implement performance monitoring'
  ],
  modules: [
    {
      id: 'performance-testing-strategy',
      title: 'Performance Testing Strategy',
      description: 'Designing comprehensive performance testing strategies',
      content: `## Performance Testing Strategy

Effective performance testing requires a strategic approach that aligns with business requirements, user expectations, and system architecture.

### Performance Testing Types

**Load Testing**: Validate system behavior under expected user loads to ensure normal operation.

**Stress Testing**: Determine system breaking points and behavior under extreme conditions.

**Spike Testing**: Test system response to sudden load increases and traffic spikes.

**Volume Testing**: Verify system performance with large amounts of data.

### Requirements and Planning

**Performance Requirements**: Define response time, throughput, and resource utilization targets.

**Test Environment**: Design test environments that accurately represent production systems.

**Test Data**: Create realistic test datasets that reflect production data patterns.

**Monitoring Strategy**: Implement comprehensive monitoring to capture system behavior during tests.`,
      order: 1,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'intermediate',
      objectives: [
        'Design performance test plans',
        'Identify performance requirements',
        'Choose appropriate testing tools'
      ]
    },
    {
      id: 'load-testing-tools',
      title: 'Load Testing Tools & Techniques',
      description: 'Using various load testing tools and techniques',
      content: 'Master k6, JMeter, and other load testing tools for different scenarios.',
      order: 2,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'intermediate',
      objectives: [
        'Use k6 for modern load testing',
        'Implement realistic test scenarios',
        'Configure different load patterns'
      ]
    },
    {
      id: 'performance-analysis',
      title: 'Performance Analysis & Optimization',
      description: 'Analyzing results and identifying optimization opportunities',
      content: 'Learn to analyze performance test results and identify bottlenecks.',
      order: 3,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Analyze performance test results',
        'Identify system bottlenecks',
        'Recommend optimization strategies'
      ]
    },
    {
      id: 'performance-monitoring',
      title: 'Continuous Performance Monitoring',
      description: 'Setting up ongoing performance monitoring and alerting',
      content: 'Implement continuous performance monitoring and automated alerting systems.',
      order: 4,
      estimatedTime: '2 hours',
      duration: '2 hours',
      difficulty: 'advanced',
      objectives: [
        'Set up performance monitoring',
        'Create performance dashboards',
        'Implement automated alerting'
      ]
    }
  ]
};

export const TESTING_MODERN_COURSES = [
  jestAdvancedCourse,
  playwrightCourse,
  performanceTestingCourse
];